import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { motion } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import API from '../services/API'
import TenantReadyMessage from './TenantReadyMessage'
import SignUpStepper from './SignUpStepper'
import PlanSelection from './PlanSelection'
import PlanSummaryAcceptance, {
  EMPTY_ACCEPTANCE,
  NO_CONFIRMATIONS,
  isAcceptanceComplete,
} from './PlanSummaryAcceptance'
import { signupCardSx, signupPalette } from './signupTheme'

function useQuery() {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

const SupportDetails = () => (
  <>
    Please contact AmbuNet on{' '}
    <a href="tel:03000570620" style={{ color: 'inherit' }}>
      0300 057 0620
    </a>{' '}
    or email us at{' '}
    <a href="mailto:support@ambunet.co.uk" style={{ color: 'inherit' }}>
      support@ambunet.co.uk
    </a>
  </>
)

// Handles everything after the verification email is clicked:
//   1. marks the email verified (bot/deliverability gate — unchanged)
//   2. shows the payment step: the plan, its server-generated Plan Summary
//      and the Business Subscription Agreement confirmations; records the
//      acceptance, then redirects to Stripe Checkout
//   3. on checkout success return (?session_id=...), asks the server to
//      verify the session and provision the tenant, then shows the ready page
//   4. on checkout cancel (?checkout=cancelled), returns to plan selection
const Verify = () => {
  const query = useQuery()
  const partialSignUpID = query.get('ref')
  const sessionId = query.get('session_id')
  const checkoutCancelled = query.get('checkout') === 'cancelled'

  // 'loading' | 'invalid' | 'payment' | 'provisioning' | 'ready' | 'error'
  const [phase, setPhase] = useState('loading')
  const [companyName, setCompanyName] = useState('')
  const [tenantUrl, setTenantUrl] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [selectedLookupKey, setSelectedLookupKey] = useState(null)
  const [plans, setPlans] = useState([])
  const [plansLoaded, setPlansLoaded] = useState(false)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  // Agreement acceptance on the payment step. `agreementSummary` is what the
  // server sent for the summary on screen ({ lookupKey, agreementVersion,
  // fingerprint, accepted }): the version and fingerprint are only ever
  // echoed back to the server, never made up here.
  const [acceptance, setAcceptance] = useState(EMPTY_ACCEPTANCE)
  const [agreementSummary, setAgreementSummary] = useState(null)
  const [summaryReloadKey, setSummaryReloadKey] = useState(0)
  const [confirmationRequired, setConfirmationRequired] = useState(false)
  const [agreementNotice, setAgreementNotice] = useState('')
  const [acceptanceErrors, setAcceptanceErrors] = useState({})
  const pollTimer = useRef(null)
  const startedRef = useRef(false)

  const activeStep =
    phase === 'ready' ? 5 : phase === 'provisioning' ? 4 : phase === 'payment' ? 3 : 2

  const clearPoll = () => {
    if (pollTimer.current) {
      window.clearInterval(pollTimer.current)
      pollTimer.current = null
    }
  }

  const startProvisionPoll = () => {
    clearPoll()
    let attempts = 0
    pollTimer.current = window.setInterval(async () => {
      attempts += 1
      const res = await API.get('/new-tenant-signup-state/' + partialSignUpID)
      if (res.status === 200 && res.data?.status === 'complete') {
        clearPoll()
        setTenantUrl(res.data.url)
        setPhase('ready')
        return
      }
      if (attempts >= 40) {
        // ~2 minutes — hand over to support rather than spinning forever
        clearPoll()
        setErrorMessage(
          'Your payment was received but setup is taking longer than expected.',
        )
        setPhase('error')
      }
    }, 3000)
  }

  const completeCheckout = async () => {
    setPhase('provisioning')
    const res = await API.post('/complete-new-tenant/' + partialSignUpID, {
      sessionId,
    })

    if (res.status === 200 && res.data?.url) {
      setTenantUrl(res.data.url)
      setPhase('ready')
      return
    }
    if (res.status === 202) {
      // Another request is provisioning — poll until it finishes
      startProvisionPoll()
      return
    }
    if (res.status === 402) {
      setErrorMessage(
        'Your payment has not been confirmed yet, so your system has not been created. If you completed checkout, please try again in a moment.',
      )
      setPhase('payment')
      return
    }
    if (res.status === 400) {
      setErrorMessage(
        'We could not match your checkout to this signup. Please try the payment again.',
      )
      setPhase('payment')
      return
    }
    if (res.status === 404) {
      setPhase('invalid')
      return
    }
    setErrorMessage(
      'Something went wrong while setting up your organisation. Your payment was received — please retry, and contact us if this continues.',
    )
    setPhase('error')
  }

  useEffect(() => {
    if (!partialSignUpID) {
      setPhase('invalid')
      return undefined
    }
    if (startedRef.current) return undefined
    startedRef.current = true

    API.get('/billing/plans')
      .then((res) => {
        if (res.status === 200 && res.data?.data) setPlans(res.data.data)
      })
      .finally(() => setPlansLoaded(true))

    const run = async () => {
      const stateRes = await API.get(
        '/new-tenant-signup-state/' + partialSignUpID,
      )

      if (stateRes.status === 404) {
        setPhase('invalid')
        return
      }
      if (stateRes.status !== 200) {
        setErrorMessage('We could not load your signup. Please try again.')
        setPhase('error')
        return
      }

      setCompanyName(stateRes.data.companyName)
      setSelectedLookupKey(stateRes.data.lookupKey ?? null)

      if (stateRes.data.status === 'complete') {
        setTenantUrl(stateRes.data.url)
        setPhase('ready')
        return
      }

      if (sessionId) {
        await completeCheckout()
        return
      }

      if (stateRes.data.status === 'provisioning') {
        setPhase('provisioning')
        startProvisionPoll()
        return
      }

      // Normal email-link arrival (or cancelled checkout return): mark the
      // address verified, then offer payment.
      const verifyRes = await API.post(
        '/verify-new-tenant/' + partialSignUpID,
      )
      if (verifyRes.status === 200) {
        if (verifyRes.data?.status === 'complete' && verifyRes.data?.url) {
          setTenantUrl(verifyRes.data.url)
          setPhase('ready')
          return
        }
        setSelectedLookupKey(
          verifyRes.data?.lookupKey ?? stateRes.data.lookupKey ?? null,
        )
        setPhase('payment')
        return
      }
      if (verifyRes.status === 404) {
        setPhase('invalid')
        return
      }
      setErrorMessage('We could not verify your email. Please try again.')
      setPhase('error')
    }

    run()

    return clearPoll
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Only a summary for the plan currently selected counts: while a newly
  // chosen plan's summary is still loading there is nothing to accept.
  const summaryForPlan =
    agreementSummary?.lookupKey === selectedLookupKey ? agreementSummary : null
  const agreementReady =
    Boolean(summaryForPlan) &&
    (summaryForPlan.accepted || isAcceptanceComplete(acceptance))

  const changePlan = (lookupKey) => {
    if (lookupKey === selectedLookupKey) return
    setSelectedLookupKey(lookupKey)
    setErrorMessage('')
    setAgreementNotice('')
    setAcceptanceErrors({})
  }

  // Responses that mean this signup is no longer waiting for payment. Shared
  // by the Plan Summary fetch, the acceptance and the checkout call; returns
  // true when the page has moved on to another phase.
  const handleSignupMoved = (res) => {
    const code = res?.data?.error
    if (res?.status === 404 && code === 'noSuchTenant') {
      setPhase('invalid')
      return true
    }
    if (res?.status === 409 && code === 'alreadyProvisioned') {
      setTenantUrl(res.data.url)
      setPhase('ready')
      return true
    }
    if (res?.status === 409 && code === 'provisioningInProgress') {
      setPhase('provisioning')
      startProvisionPoll()
      return true
    }
    return false
  }

  // The server holds no acceptance matching what is about to be bought (the
  // agreement version, the price or the plan has moved on). That is not a
  // failure: reload the summary, clear the ticks and ask again.
  const askToConfirmAgain = (message) => {
    setAcceptance((current) => ({ ...current, ...NO_CONFIRMATIONS }))
    setAgreementSummary(null)
    setConfirmationRequired(true)
    setSummaryReloadKey((key) => key + 1)
    setAgreementNotice(message)
  }

  const handleCheckoutFailure = (res) => {
    const code = res?.data?.error

    if (handleSignupMoved(res)) return

    if (res?.status === 409 && code === 'agreementVersionChanged') {
      askToConfirmAgain(
        'The AmbuNet Business Subscription Agreement has been updated since this page was opened, so we have refreshed the details above. Please read the current version and confirm again. No payment has been taken.',
      )
      return
    }
    if (res?.status === 409 && code === 'planSummaryChanged') {
      askToConfirmAgain(
        'Your Plan Summary has changed since it was shown to you, so we have refreshed it above. Please review it and confirm again. No payment has been taken.',
      )
      return
    }
    if (res?.status === 409 && code === 'agreementNotAccepted') {
      askToConfirmAgain(
        'We need your confirmation of the Agreement for this plan before checkout, so we have refreshed your Plan Summary above. Please review it and confirm again. No payment has been taken.',
      )
      return
    }
    if (res?.status === 400 && code === 'confirmationsRequired') {
      // The server did not receive all three ticks: clear them so each box
      // is flagged and can be ticked again.
      setAcceptance((current) => ({ ...current, ...NO_CONFIRMATIONS }))
      setAcceptanceErrors({ confirmations: true })
      setErrorMessage(
        'Please tick all three confirmations above, then continue to checkout.',
      )
      return
    }
    if (res?.status === 400 && code === 'roleRequired') {
      setAcceptanceErrors({
        role: 'Please enter your role in the organisation (2 to 120 characters).',
      })
      setErrorMessage(
        'Please enter your role in the organisation above, then continue to checkout.',
      )
      return
    }
    if (res?.status === 409 && code === 'emailNotVerified') {
      setErrorMessage(
        'Your email address has not been verified yet. Please refresh this page to verify it, then continue to checkout.',
      )
      return
    }
    if (res?.status === 400 && code === 'invalidPlan') {
      setErrorMessage('Please choose a subscription plan to continue.')
      return
    }
    if (res?.status === 400 && code === 'priceNotFound') {
      setErrorMessage(
        'We could not find the current price for this plan. Please choose another plan or try again shortly.',
      )
      return
    }
    if (res?.status === 429) {
      setErrorMessage(
        'Too many attempts have been made in a short time. Please wait a few minutes and try again. No payment has been taken.',
      )
      return
    }
    setErrorMessage(
      'We could not open the secure checkout. Please try again, and contact us if this continues.',
    )
  }

  const goToCheckout = async () => {
    if (!selectedLookupKey || !summaryForPlan || !agreementReady) return
    setCheckoutLoading(true)
    setErrorMessage('')
    setAgreementNotice('')
    setAcceptanceErrors({})

    try {
      // The acceptance is recorded first: checkout is refused unless the
      // latest acceptance is for exactly the plan and price being bought.
      // The version and fingerprint are the ones the server sent with the
      // summary on screen; the server regenerates the summary and compares.
      if (!summaryForPlan.accepted) {
        const acceptRes = await API.post(
          '/new-tenant-accept-agreement/' + partialSignUpID,
          {
            lookupKey: selectedLookupKey,
            agreementVersion: summaryForPlan.agreementVersion,
            fingerprint: summaryForPlan.fingerprint,
            acceptedByRole: acceptance.role.trim(),
            confirmations: {
              reviewedAgreement: acceptance.reviewedAgreement,
              acceptsForOrganisation: acceptance.acceptsForOrganisation,
              hasAuthority: acceptance.hasAuthority,
            },
          },
          { skipGlobalErrorHandler: true },
        )
        if (acceptRes.status !== 200 || acceptRes.data?.accepted !== true) {
          setCheckoutLoading(false)
          handleCheckoutFailure(acceptRes)
          return
        }
      }

      const res = await API.post(
        '/new-tenant-checkout/' + partialSignUpID,
        { lookupKey: selectedLookupKey },
        { skipGlobalErrorHandler: true },
      )
      if (res.status === 200 && res.data?.url) {
        window.location.href = res.data.url
        return
      }
      setCheckoutLoading(false)
      handleCheckoutFailure(res)
    } catch (e) {
      // Network failure or timeout: nothing was charged, so let them retry.
      setCheckoutLoading(false)
      handleCheckoutFailure(null)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#F7F9FC',
        background: 'linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        p: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{ width: '100%', maxWidth: 980 }}
      >
        <Box
          component={Paper}
          elevation={0}
          sx={{
            ...signupCardSx,
            // The signup app has no global border-box reset: without this the
            // card is its padding wider than the page and a phone scrolls
            // sideways.
            boxSizing: 'border-box',
            justifyContent: 'flex-start',
            p: { xs: 2.5, md: 4 },
            mt: { xs: 1, md: 4 },
          }}
        >
          <SignUpStepper activeStep={activeStep} />

          {companyName && phase !== 'invalid' && (
            <Typography
              textAlign="center"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.4rem', md: '1.8rem' },
                letterSpacing: '-0.02em',
                color: signupPalette.primary.main,
                mb: 2,
              }}
            >
              {companyName}
            </Typography>
          )}

          {phase === 'loading' && (
            <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
              <CircularProgress sx={{ color: signupPalette.third.main }} />
              <Typography color="text.secondary">
                Verifying your email address…
              </Typography>
            </Stack>
          )}

          {phase === 'invalid' && (
            <Alert severity="error" icon={false} variant="filled">
              <AlertTitle>The link you have used is invalid.</AlertTitle>
              <SupportDetails />
            </Alert>
          )}

          {phase === 'payment' && (
            <Stack spacing={2.5}>
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                justifyContent="center"
              >
                <MarkEmailReadRoundedIcon
                  sx={{ color: signupPalette.third.main, fontSize: 28 }}
                />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    color: signupPalette.primary.main,
                  }}
                >
                  Email verified — accept the agreement and pay
                </Typography>
              </Stack>

              {checkoutCancelled && (
                <Alert severity="info">
                  Checkout was cancelled and no payment was taken. When
                  you&apos;re ready, review your plan below and continue to
                  payment — your signup details are saved.
                </Alert>
              )}

              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Confirm your plan, review your Plan Summary and accept the
                AmbuNet Business Subscription Agreement on behalf of your
                organisation, then complete payment through Stripe&apos;s
                secure checkout. Your AmbuNet system is created as soon as
                payment succeeds.
              </Typography>

              <PlanSelection
                value={selectedLookupKey}
                onChange={changePlan}
                plans={plans}
                plansLoaded={plansLoaded}
              />

              <PlanSummaryAcceptance
                partialSignUpID={partialSignUpID}
                lookupKey={selectedLookupKey}
                companyName={companyName}
                reloadKey={summaryReloadKey}
                requireConfirmation={confirmationRequired}
                value={acceptance}
                onChange={setAcceptance}
                onSummaryChange={setAgreementSummary}
                onSignupMoved={handleSignupMoved}
                errors={acceptanceErrors}
                disabled={checkoutLoading}
              />

              {agreementNotice && (
                <Alert severity="info">{agreementNotice}</Alert>
              )}

              {errorMessage && <Alert severity="warning">{errorMessage}</Alert>}

              <Stack alignItems="center" spacing={1}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={goToCheckout}
                  disabled={
                    checkoutLoading || !selectedLookupKey || !agreementReady
                  }
                  startIcon={
                    checkoutLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <LockRoundedIcon />
                    )
                  }
                  sx={{
                    bgcolor: signupPalette.third.main,
                    '&:hover': { bgcolor: signupPalette.third.dark },
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 4,
                    py: 1.25,
                  }}
                >
                  Continue to Secure Checkout
                </Button>
                {summaryForPlan && !agreementReady && (
                  <Typography
                    variant="caption"
                    textAlign="center"
                    sx={{ fontWeight: 600, color: signupPalette.secondary.main }}
                  >
                    Enter your role and tick all three confirmations above to
                    continue.
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                >
                  Payments are processed by Stripe. VAT is added at checkout
                  where applicable.
                </Typography>
              </Stack>
            </Stack>
          )}

          {phase === 'provisioning' && (
            <Stack alignItems="center" spacing={2.5} sx={{ py: 5 }}>
              <CircularProgress
                size={56}
                sx={{ color: signupPalette.third.main }}
              />
              <Stack spacing={0.75} alignItems="center" textAlign="center">
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.2rem',
                    color: signupPalette.primary.main,
                  }}
                >
                  Payment confirmed — building your system
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 440 }}
                >
                  We&apos;re provisioning your organisation&apos;s dedicated
                  AmbuNet environment. This usually takes less than a minute —
                  please keep this page open.
                </Typography>
              </Stack>
            </Stack>
          )}

          {phase === 'ready' && <TenantReadyMessage url={tenantUrl} />}

          {phase === 'error' && (
            <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
              <Alert severity="error" sx={{ width: '100%' }}>
                <AlertTitle>{errorMessage || 'Something went wrong.'}</AlertTitle>
                <SupportDetails />
              </Alert>
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Try Again
              </Button>
            </Stack>
          )}
        </Box>
      </motion.div>
    </Box>
  )
}

export default Verify
